'use client'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { ShieldCheck, Zap, Lock, Power, Code2, Sparkles } from 'lucide-react'
import Link from 'next/link'

const faqs = [
  {
    question: '¿LuckAgents es nuevo? ¿Por qué debería probarlo?',
    answer:
      'Sí, estamos en lanzamiento. Eso significa que como early adopter tendrás: acceso directo al equipo fundador, tu feedback moldeará el producto, precio de lanzamiento bloqueado para siempre, y onboarding personalizado incluido. Además, tienes 14 días para probarlo gratis y 30 días de garantía de devolución.',
    icon: Sparkles,
  },
  {
    question: '¿Me pueden bloquear el número de WhatsApp?',
    answer:
      'No si usas LuckAgents. Somos un proveedor que cumple 100% con las políticas de Meta y WhatsApp Business 2026. Evitamos comportamientos de spam y usamos la API oficial, lo que protege tu reputación.',
    icon: ShieldCheck,
  },
  {
    question: '¿Necesito saber programar o contratar desarrolladores?',
    answer:
      'Para nada. LuckAgents es una plataforma No-Code. Configuras tus flujos, respuestas y horarios desde un panel visual intuitivo. Si sabes usar WhatsApp, sabes usar LuckAgents.',
    icon: Code2,
  },
  {
    question: '¿Puedo pausar el bot y responder yo mismo?',
    answer:
      "Sí, en cualquier momento. Tienes un 'Modo Híbrido' donde el bot maneja lo rutinario, pero puedes tomar el control de la conversación con un clic cuando el cliente requiera atención humana experta.",
    icon: Power,
  },
  {
    question: '¿Con qué herramientas y CRMs se integra?',
    answer:
      'Nos conectamos nativamente con HubSpot, Salesforce, Google Calendar, Notion y más. También tenemos API abierta y webhooks para integraciones a medida con tu ERP o base de datos.',
    icon: Zap,
  },
  {
    question: '¿Dónde se guardan los datos? ¿Es seguro?',
    answer:
      'Tus datos están encriptados (E2E) y almacenados en servidores seguros. Cumplimos con GDPR y normativas locales de protección de datos para que tu información y la de tus clientes esté a salvo.',
    icon: Lock,
  },
  {
    question: '¿Puedo cancelar cuando quiera?',
    answer:
      'Sí, sin penalización. Cancela desde tu panel en cualquier momento con un solo clic. Si estás en prueba gratuita, no hay cobro alguno. Queremos que te quedes por los resultados, no por obligación.',
    icon: Power,
  },
]

export function FAQSection() {
  return (
    <section
      aria-labelledby="faq-heading"
      className="bg-background relative overflow-hidden py-24 md:py-32"
    >
      <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 text-center">
          <p className="text-primary mb-3 text-sm font-semibold uppercase tracking-[0.16em]">
            Resolvemos tus dudas
          </p>
          <h2
            id="faq-heading"
            className="text-foreground text-balance text-3xl font-semibold tracking-tight sm:text-4xl"
          >
            Preguntas frecuentes antes de empezar
          </h2>
        </div>

        <div className="grid gap-4">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={faq.question}
                value={`item-${index + 1}`}
                className="border-border/80 bg-card/50 hover:bg-card dark:border-border/60 rounded-2xl border px-6 py-2 transition hover:shadow-sm"
              >
                <AccordionTrigger className="text-foreground hover:text-primary gap-4 text-left text-base font-semibold hover:no-underline">
                  <div className="flex items-center gap-4">
                    <div className="bg-primary/5 text-primary hidden h-10 w-10 shrink-0 items-center justify-center rounded-full sm:flex">
                      <faq.icon className="h-5 w-5" />
                    </div>
                    <span>{faq.question}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-foreground/70 pb-6 text-base leading-relaxed sm:pl-14">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Zero Risk Guarantee Block */}
        <div className="mx-auto mb-8 mt-12 max-w-2xl rounded-2xl border border-emerald-100 bg-emerald-50/80 p-6 text-center backdrop-blur-sm dark:border-emerald-900/20 dark:bg-emerald-900/10">
          <div className="mb-2 flex items-center justify-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            <h4 className="text-lg font-bold text-emerald-800 dark:text-emerald-400">
              Garantía de Riesgo Cero
            </h4>
          </div>
          <p className="text-sm font-medium text-emerald-700/90 dark:text-emerald-300/80">
            14 días de prueba gratis · 30 días garantía de devolución · Cancela con un clic
          </p>
        </div>

        <div className="bg-linear-to-br relative mt-8 overflow-hidden rounded-3xl border border-slate-200 from-slate-50 to-slate-100 p-8 text-center dark:border-white/10 dark:from-white/5 dark:to-white/[0.02]">
          {/* Decorative elements */}
          <div className="absolute right-0 top-0 h-32 w-32 rounded-full bg-blue-500/10 blur-3xl" />
          <div className="absolute bottom-0 left-0 h-32 w-32 rounded-full bg-cyan-500/10 blur-3xl" />

          <div className="relative z-10">
            <h3 className="mb-3 text-xl font-bold">¿Tienes una duda técnica específica?</h3>
            <p className="text-muted-foreground mx-auto mb-8 max-w-lg">
              Nuestro equipo de ingeniería puede revisar tu caso de uso gratis.
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Button asChild size="lg" className="h-12 rounded-full px-6">
                <Link href="/contacto">Hablar con un experto</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
